import { useEffect, useState } from "react";
import { Plus, Frame } from "lucide-react";
import { API, fmtPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Seo from "@/components/Seo";
import ArtworkCard from "@/components/ArtworkCard";
import FramedPreview from "@/components/FramedPreview";
import { Reveal, Eyebrow, HeroLines } from "@/components/Reveal";

export default function Portfolio() {
  const [artworks, setArtworks] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const { add } = useCart();

  useEffect(() => {
    Promise.all([
      API.get("/artworks").then((r) => setArtworks(r.data)).catch(() => {}),
      API.get("/notecards").then((r) => setCards(r.data.filter((c) => c.status !== "soon" && c.status !== "retired"))).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const visibleArtworks = artworks;
  const visibleCards = cards;

  return (
    <div data-testid="portfolio-page">
      <Seo title="Original Works" description="Original watercolor paintings by Richard Kuensting — small signed card originals, every piece one of one." />
      <header className="max-w-7xl mx-auto px-5 sm:px-10 pt-16 sm:pt-24 pb-10">
        <Eyebrow>The Portfolio</Eyebrow>
        <HeroLines lines={["Original works,", "from the studio."]}
          className="font-serif font-light text-ink text-4xl sm:text-6xl mt-4 leading-[1.05] tracking-tight" />
        <p className="text-stone2 mt-6 max-w-xl text-sm sm:text-base leading-relaxed">
          Every piece is one of one — small signed originals the size of a letter, painted one at a time. No prints, no reproductions.
        </p>
      </header>

      <section className="max-w-7xl mx-auto px-5 sm:px-10 py-14 sm:py-20">
        {loading ? (
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-stone2" data-testid="portfolio-loading">Unhanging the gallery…</p>
        ) : (
          <>
            {visibleArtworks.length > 0 && (
              <div className="grid md:grid-cols-12 gap-6 md:gap-8 mb-16" data-testid="portfolio-artworks">
                {visibleArtworks.map((art, i) => (
                  <ArtworkCard key={art.id} art={art} index={i} className="md:col-span-7" />
                ))}
              </div>
            )}
            {visibleCards.length > 0 && (
              <>
                <Reveal className="mb-10">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">Small originals — the card table</p>
                  <p className="text-sm text-stone2 mt-2 max-w-xl">Signed one-of-one watercolors on 140 lb Arches — $7 each with free mailing, suitable for framing.</p>
                </Reveal>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8" data-testid="portfolio-cards">
                  {visibleCards.map((card, i) => (
                    <Reveal key={card.id} delay={(i % 4) * 0.07}>
                      <article className="group" data-testid={`portfolio-card-${card.id}`}>
                        <button type="button" onClick={() => setPreview(card)} data-testid={`portfolio-card-preview-${card.id}`}
                          aria-label={`See ${card.title} framed`} className="block w-full text-left cursor-pointer">
                          <div className="relative overflow-hidden bg-[#E8E0D0] border border-ink/10 deckle aspect-[4/5]">
                            <img src={card.image} alt={`${card.title} — original watercolor by Richard Kuensting`} loading="lazy"
                              className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05] ${card.status === "sold" ? "opacity-70 grayscale-[35%]" : ""}`} />
                            <span className={`absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border backdrop-blur ${card.status === "sold" ? "border-stone2/40 bg-paper/85 text-stone2" : card.status === "soon" ? "border-ochre/40 bg-paper/85 text-ochre" : "border-terracotta/30 bg-paper/85 text-terracotta"}`}>
                              {card.status === "sold" ? "Sold" : card.status === "soon" ? "Soon" : "One of one"}
                            </span>
                          </div>
                        </button>
                        <div className="mt-4">
                          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">Card original · signed</p>
                          <h3 className="font-serif text-lg sm:text-xl text-ink mt-1 leading-snug">{card.title}</h3>
                          <div className="flex items-center justify-between mt-3 gap-3">
                            <p className="font-serif text-xl text-terracotta">{card.status === "sold" ? "—" : fmtPrice(card.price_cents)}</p>
                            {card.status === "sold" ? (
                              <span data-testid={`portfolio-sold-${card.id}`} className="inline-flex items-center gap-1.5 border border-stone2/30 rounded-full px-4 py-2 text-[10px] font-mono uppercase tracking-[0.15em] text-stone2">
                                Found its home
                              </span>
                            ) : (
                              <button
                                data-testid={`portfolio-add-to-cart-${card.id}`}
                                onClick={() => add(card)}
                                className="inline-flex items-center gap-1.5 border border-ink/20 rounded-full px-4 py-2 text-[10px] font-mono uppercase tracking-[0.15em] hover:bg-ink hover:text-linen transition-colors duration-300"
                              >
                                <Plus size={12} /> Add
                              </button>
                            )}
                          </div>
                          {card.status === "available" && (
                            <button
                              data-testid={`portfolio-see-framed-${card.id}`}
                              onClick={() => setPreview(card)}
                              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-stone2 hover:text-terracotta transition-colors py-1.5"
                            >
                              <Frame size={12} /> See it framed
                            </button>
                          )}
                        </div>
                      </article>
                    </Reveal>
                  ))}
                </div>
              </>
            )}
            {visibleArtworks.length === 0 && visibleCards.length === 0 && (
              <p className="font-serif text-2xl text-stone2" data-testid="portfolio-empty">Nothing in this corner yet — new works are on the easel.</p>
            )}
          </>
        )}
      </section>

      <FramedPreview card={preview} open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }} />
    </div>
  );
}
